import { useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';
import { ExceptionUtils, ExceptionTypes, ExceptionApi } from '@frontend/api-schedule-exceptions';
import { useTranslation } from '@frontend/i18n';
import { useModalControl, ConfirmationModal, useAlert, Button } from '@frontend/design-system';
import { shouldInvalidateExceptions } from '../../utils';
import { ScheduleExceptionsModal } from '../ScheduleExceptionsModal';
import { ScheduleExceptionsTable } from '../ScheduleExceptionsTable';
import * as styles from './styles';

interface Props {
  locationId: string;
  data?: ExceptionTypes.Exception[];
  providerId?: string;
  filters: ExceptionTypes.ExceptionTableFilters;
  handleFiltersUpdate: (newFilters: ExceptionTypes.ExceptionTableFilters) => void;
  checkConflicts?: (newException: ExceptionTypes.ExceptionForm) => boolean | undefined;
}

export const ScheduleExceptions = ({
  locationId,
  data,
  providerId,
  filters,
  handleFiltersUpdate,
  checkConflicts,
}: Props) => {
  const { t } = useTranslation('schedule');
  const queryClient = useQueryClient();
  const { getDateTime, sortByDate } = ExceptionUtils;
  const alert = useAlert();

  const [selectedExceptionId, setSelectedExceptionId] = useState<string | undefined>();

  const upcomingExceptions = useMemo(() => {
    return sortByDate(data);
  }, [data]);

  const { modalProps: editModalProps, triggerProps: editTriggerProps } = useModalControl();

  const { modalProps: deleteModalProps, triggerProps: deleteTriggerProps } = useModalControl();

  const handleAddNew = () => {
    setSelectedExceptionId(undefined);
    editTriggerProps.onClick();
  };

  const exceptionsDataToDisplay = useMemo((): ExceptionTypes.ExceptionData[] => {
    return (
      upcomingExceptions?.map((exception: ExceptionTypes.Exception) => {
        const [startDate, endDate, startTime, endTime] = getDateTime(exception?.startDateTime, exception?.endDateTime);
        return {
          id: exception?.id || '',
          name: exception?.name,
          isAvailable: exception?.isAvailable ? 'true' : 'false',
          startDate,
          endDate,
          startTime,
          endTime,
        };
      }) || []
    );
  }, [upcomingExceptions]);

  const selectedException = useMemo(() => {
    if (!selectedExceptionId || !exceptionsDataToDisplay) {
      return {};
    }
    return exceptionsDataToDisplay?.find(({ id }) => id === selectedExceptionId);
  }, [selectedExceptionId, exceptionsDataToDisplay]);

  const handleEdit = (exceptionId?: string, e?: React.MouseEvent<HTMLElement, MouseEvent>) => {
    e?.stopPropagation();
    setSelectedExceptionId(exceptionId);
    editTriggerProps.onClick();
  };

  const handleDelete = (exceptionId?: string, e?: React.MouseEvent<HTMLElement, MouseEvent>) => {
    e?.stopPropagation();
    setSelectedExceptionId(exceptionId);
    deleteTriggerProps.onClick();
  };

  const handleConfirmDelete = async () => {
    if (!selectedExceptionId) {
      return;
    }
    try {
      await ExceptionApi.deleteException(locationId, selectedExceptionId, providerId);
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) => shouldInvalidateExceptions(queryKey, locationId, providerId),
      });
      alert.success(t('Successfully deleted exception'));
    } catch (error: any) {
      console.error(error);
      alert.error(t('Failed to delete exception'));
    } finally {
      setSelectedExceptionId(undefined);
    }
  };

  return (
    <>
      <Button variant='primary' size='large' css={styles.primaryButton} {...editTriggerProps} onClick={handleAddNew}>
        {t('Add Exception')}
      </Button>

      <ScheduleExceptionsTable
        exceptionData={exceptionsDataToDisplay}
        handleDelete={handleDelete}
        handleEdit={handleEdit}
        filters={filters}
        handleFiltersUpdate={handleFiltersUpdate}
      />

      <ScheduleExceptionsModal
        {...editModalProps}
        data={selectedException as ExceptionTypes.ExceptionData}
        locationId={locationId}
        providerId={providerId}
        checkConflicts={checkConflicts}
      />

      <ConfirmationModal
        {...deleteModalProps}
        title={t('Delete Exception')}
        destructive
        message={t('Are you sure you want to delete this exception?')}
        confirmLabel={t('Delete')}
        cancelLabel={t('Cancel')}
        onCancel={deleteModalProps.onClose}
        onConfirm={handleConfirmDelete}
      />
    </>
  );
};
